import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ListaPrecioSearch from './ListaPrecioSearch';
import listaPrecioService, { ListaPrecio } from '../services/listaPrecioService';
import storeCustomerAccessService, {
  StoreCustomerAccess,
} from '../services/storeCustomerAccessService';

interface Props {
  nroCliente: number;
  customerEmail?: string | null;
  commercialPriceListId?: string | null;
}

const statusLabel: Record<string, string> = {
  disabled: 'No habilitado',
  enabled: 'Habilitado (sin invitación)',
  invited: 'Invitación enviada',
  active: 'Cuenta activa',
};

const statusColor: Record<string, 'default' | 'warning' | 'info' | 'success'> = {
  disabled: 'default',
  enabled: 'warning',
  invited: 'info',
  active: 'success',
};

const ClienteEcommercePanel: React.FC<Props> = ({
  nroCliente,
  customerEmail,
  commercialPriceListId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [access, setAccess] = useState<StoreCustomerAccess | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [priceListId, setPriceListId] = useState('');
  const [listaSeleccionada, setListaSeleccionada] = useState<Pick<ListaPrecio, 'codigoLista' | 'descripcion'> | null>(null);
  const [openListaSearch, setOpenListaSearch] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await storeCustomerAccessService.getAccess(nroCliente);
      setAccess(data);
      setEnabled(data.enabled);
      setPriceListId(data.priceListId || '');
      if (data.priceListId) {
        try {
          const lista = await listaPrecioService.getListaPrecio(data.priceListId);
          setListaSeleccionada({ codigoLista: lista.codigoLista, descripcion: lista.descripcion });
        } catch {
          setListaSeleccionada({ codigoLista: data.priceListId, descripcion: '' });
        }
      } else {
        setListaSeleccionada(null);
      }
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'No se pudo cargar el acceso ecommerce', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [nroCliente, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await storeCustomerAccessService.upsertAccess(nroCliente, {
        enabled,
        priceListId: priceListId || null,
      });
      setAccess(data);
      enqueueSnackbar('Acceso ecommerce actualizado', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async () => {
    setInviting(true);
    try {
      if (!enabled) {
        await storeCustomerAccessService.upsertAccess(nroCliente, {
          enabled: true,
          priceListId: priceListId || null,
        });
        setEnabled(true);
      }
      await storeCustomerAccessService.invite(nroCliente);
      enqueueSnackbar('Invitación enviada por email', { variant: 'success' });
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'No se pudo enviar la invitación', {
        variant: 'error',
      });
    } finally {
      setInviting(false);
    }
  };

  const handleDisable = async () => {
    setSaving(true);
    try {
      const data = await storeCustomerAccessService.disable(nroCliente);
      setAccess(data);
      setEnabled(false);
      enqueueSnackbar('Acceso deshabilitado y sesiones revocadas', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al deshabilitar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Typography variant="h6">eCommerce</Typography>
        <Chip
          size="small"
          label={statusLabel[access?.status || 'disabled'] || access?.status}
          color={statusColor[access?.status || 'disabled'] || 'default'}
        />
      </Stack>

      {!customerEmail && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          El cliente necesita un email en la ficha para habilitar e invitar a la tienda.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                disabled={!customerEmail && !enabled}
              />
            }
            label="Habilitar en tienda online"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Lista de precios ecommerce (opcional)"
            value={
              listaSeleccionada
                ? `${listaSeleccionada.codigoLista.trim()} - ${listaSeleccionada.descripcion?.trim() || ''}`
                : priceListId || ''
            }
            fullWidth
            margin="normal"
            helperText={
              commercialPriceListId
                ? `Si queda vacío, se usa la lista comercial (${commercialPriceListId}).`
                : 'Si queda vacío, se usa la lista comercial del cliente.'
            }
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setOpenListaSearch(true)} edge="end" color="primary">
                    <ArrowDropDownIcon />
                  </IconButton>
                  {priceListId && (
                    <Button
                      size="small"
                      onClick={() => {
                        setPriceListId('');
                        setListaSeleccionada(null);
                      }}
                    >
                      Limpiar
                    </Button>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="body2" color="text.secondary" mt={2}>
            Email invitación: <strong>{access?.invitedEmail || customerEmail || '—'}</strong>
          </Typography>
          {access?.invitedAt && (
            <Typography variant="body2" color="text.secondary">
              Invitado: {new Date(access.invitedAt).toLocaleString()}
            </Typography>
          )}
          {access?.activatedAt && (
            <Typography variant="body2" color="text.secondary">
              Activado: {new Date(access.activatedAt).toLocaleString()}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar acceso'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleInvite}
              disabled={inviting || !customerEmail}
            >
              {inviting ? 'Enviando…' : access?.invitedAt ? 'Reenviar invitación' : 'Enviar invitación'}
            </Button>
            {access?.enabled && (
              <Button variant="outlined" color="error" onClick={handleDisable} disabled={saving}>
                Deshabilitar
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>

      <ListaPrecioSearch
        open={openListaSearch}
        onClose={() => setOpenListaSearch(false)}
        onSelect={(lista) => {
          setListaSeleccionada({ codigoLista: lista.codigoLista, descripcion: lista.descripcion });
          setPriceListId(lista.codigoLista.trim());
          setOpenListaSearch(false);
        }}
      />
    </Box>
  );
};

export default ClienteEcommercePanel;
